// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faQuestion, faChevronDown, faChevronUp, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import { useJlptLevels } from "../../hooks/useJlptLevels";
import { useVocabularyProgress } from "../../hooks/useVocabularyProgress";
import { useVocabularyListenCounts } from "../../hooks/useVocabularyListenCounts";
import { fetchVocabularyLevel, wordMatchesMeaning, wordMatchesRomaji } from "../../utils/vocabularyData";
import { conjugateVerb, MASHOUKA_LABEL, TO_OMOIMASU_LABEL } from "../../utils/verbConjugation";
import { kanaToRomaji } from "../../utils/kanaToRomaji";
import { vocabularyRomaji } from "../../utils/vocabularyRomaji";
import { groupWordsByKanji } from "../../utils/vocabularyKanjiGroups";
import { buildQueue } from "./vocabularyQuizHelpers";
import { LearnedBadge, SignInToTrackLink } from "../LearningStatus/LearningStatus";
import SpeakButton from "../SpeakButton/SpeakButtonComponent";
import Seo from "../Seo/SeoComponent";
import UsuallyKanaWord from "../UsuallyKanaWord/UsuallyKanaWord";
import styles from "./vocabularyPage.module.scss";

const levelKeys = ["5", "4", "3", "2", "1"];
const statusKeys = ["all", "unlearned", "learned"];
const viewModeKeys = ["flat", "byKanji"];
const katakanaPattern = /[\u30A0-\u30FF\u31F0-\u31FF\uFF66-\uFF9D]/;

function containsKatakana(entry) {
    return katakanaPattern.test(`${entry.word} ${entry.alsoWritten || ""} ${entry.reading}`);
}

const POS_I18N_KEYS = {
    n: "n",
    "adj-i": "adj-i",
    "adj-na": "adj-na",
    "adj-no": "adj-no",
    adv: "adv",
    exp: "exp",
    int: "int",
    conj: "conj",
    prt: "prt",
    pn: "pn",
    ctr: "ctr",
    suf: "suf",
    pref: "pref",
};

// Phrase-pattern conjugation forms carry an English label from
// verbConjugation.ts; these map them to a translated one.
const CONJUGATION_FORM_I18N_KEYS = {
    [MASHOUKA_LABEL]: "mashouka",
    [TO_OMOIMASU_LABEL]: "toOmoimasu",
};

function describeWord(word, t) {
    if (word.verbType) return t("posLabels.verb");
    for (const tag of word.partsOfSpeech) {
        if (POS_I18N_KEYS[tag]) return t(`posLabels.${POS_I18N_KEYS[tag]}`);
    }
    return null;
}

// Romaji worth showing, or "" when there is nothing safe to show: a missing
// reading, or one the romaji table cannot fully convert, would otherwise
// surface as an empty or half-converted line.
function displayableRomaji(romaji) {
    return /^[A-Za-z'.-]+$/.test(romaji) ? romaji : "";
}

function ConjugationTable({ word, reading, verbType, showRomaji = false }) {
    const { t } = useTranslation("vocabularyPage");
    const forms = useMemo(() => conjugateVerb(word, reading, verbType), [word, reading, verbType]);
    if (!forms) return null;

    return (
        <table className={styles.conjugationTable}>
            <tbody>
                {forms.map((form) => {
                    const romaji = showRomaji ? displayableRomaji(kanaToRomaji(form.reading)) : "";
                    return (
                        <tr key={form.label}>
                            <th>
                                {CONJUGATION_FORM_I18N_KEYS[form.label]
                                    ? t(`conjugationForms.${CONJUGATION_FORM_I18N_KEYS[form.label]}`)
                                    : form.label}
                            </th>
                            <td>
                                <span className={styles.conjugationKanji}>{form.kanji}</span>
                                {form.kanji !== form.reading && (
                                    <span className={styles.conjugationReading}>{form.reading}</span>
                                )}
                                {romaji && (
                                    <span className={styles.conjugationRomaji} lang="ja-Latn">
                                        {romaji}
                                    </span>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

function VocabularyCard({
    entry,
    showJlptLevel,
    learned,
    isProgressReady,
    signedOut,
    onToggleLearned,
    saving,
    listenCount,
    isListenProgressReady,
    onListen,
}) {
    const { t } = useTranslation("vocabularyPage");
    const [expanded, setExpanded] = useState(false);
    const [showXpReward, setShowXpReward] = useState(false);
    useEffect(() => {
        if (!showXpReward) return undefined;
        const timer = window.setTimeout(() => setShowXpReward(false), 1800);
        return () => window.clearTimeout(timer);
    }, [showXpReward]);
    const description = describeWord(entry, t);
    const canConjugate = Boolean(entry.verbType);
    // Matches the shared LearningStatus button: once a word is learned the
    // control is labelled with the action it performs, and the badge below
    // carries the state.
    let toggleLabel = t("checking");
    if (isProgressReady) toggleLabel = learned ? t("reviewAgain") : t("markAsLearned");

    const showListenCount = !signedOut && isListenProgressReady;

    return (
        <li className={styles.vocabCard}>
            <div className={styles.vocabCardMain}>
                <div className={styles.vocabCardText}>
                    <div className={styles.vocabWordRow}>
                        <UsuallyKanaWord className={styles.vocabWord} word={entry.word} alsoWritten={entry.alsoWritten} />
                        {entry.word !== entry.reading && (
                            <span className={styles.vocabReading}>
                                {entry.reading} ({vocabularyRomaji(entry.word, entry.reading)})
                            </span>
                        )}
                        <SpeakButton
                            text={entry.reading}
                            label={t("playPronunciationOf", { word: entry.word })}
                            className={styles.vocabSpeakButton}
                            onPlay={() => onListen(entry.id)}
                        />
                        <SpeakButton
                            text={entry.reading}
                            label={t("playSlowPronunciationOf", { word: entry.word })}
                            className={styles.vocabSpeakButton}
                            onPlay={() => onListen(entry.id)}
                            slow
                        />
                    </div>
                    <p className={styles.vocabMeaning}>{entry.meanings.join(", ")}</p>
                    <div className={styles.badgeRow}>
                        {showJlptLevel && (
                            <span className={styles.jlptLevelBadge}>{t("jlptLevel", { level: entry.jlptLevel })}</span>
                        )}
                        {description && <span className={styles.posBadge}>{description}</span>}
                        {learned && <LearnedBadge />}
                    </div>
                    {showListenCount && (
                        <p className={styles.vocabListenCount}>
                            {listenCount > 0 ? (
                                <Trans
                                    t={t}
                                    i18nKey="heard"
                                    count={listenCount}
                                    components={{ num: <span className={styles.vocabListenCountNumber} /> }}
                                />
                            ) : (
                                t("notHeardYet")
                            )}
                        </p>
                    )}
                </div>
                {signedOut ? (
                    <SignInToTrackLink label={t("signInToTrack")} />
                ) : (
                    <div className={styles.learnedControl}>
                        <button
                            type="button"
                            className={learned ? styles.learnedToggleActive : styles.learnedToggle}
                            onClick={async () => {
                                const earnedXp = !learned && await onToggleLearned(entry.id, true);
                                if (learned) await onToggleLearned(entry.id, false);
                                if (earnedXp) setShowXpReward(true);
                            }}
                            disabled={!isProgressReady || saving}
                        >
                            <FontAwesomeIcon icon={learned ? faRotateLeft : faCheck} />
                            {saving ? t("savingProgress") : toggleLabel}
                        </button>
                        {showXpReward && <span className={styles.xpReward} role="status" aria-live="polite">+5 EXP</span>}
                    </div>
                )}
            </div>

            {canConjugate && (
                <div className={styles.conjugationSection}>
                    <button type="button" className={styles.conjugationToggle} onClick={() => setExpanded((v) => !v)}>
                        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
                        {expanded ? t("hideConjugations") : t("showConjugations")}
                    </button>
                    {expanded && (
                        <ConjugationTable word={entry.word} reading={entry.reading} verbType={entry.verbType} />
                    )}
                </div>
            )}
        </li>
    );
}

function VocabQuizRound({ queue, onMark, onFinish, onExit }) {
    const { t } = useTranslation("vocabularyPage");
    const [index, setIndex] = useState(0);
    const [phase, setPhase] = useState("question"); // question | feedback
    const [lastStatus, setLastStatus] = useState(null);
    const [tally, setTally] = useState({ memorized: 0, notMemorized: 0 });

    const entry = queue[index];
    /* istanbul ignore if -- defensive: startQuiz never opens a round with an empty queue, and the last answer finishes the round instead of advancing past the end */
    if (!entry) return null;
    const description = describeWord(entry, t);
    // A word with a separate reading already shows "reading (romaji)" while the
    // question is open. A kana-only word has no such line, so its romaji
    // appears only once it has been answered, not before.
    const entryRomaji = displayableRomaji(vocabularyRomaji(entry.word, entry.reading));
    const kanaOnlyRomaji = entry.word === entry.reading && phase === "feedback" ? entryRomaji : "";

    function handleAnswer(status) {
        onMark(entry.id, status === "memorized");
        setLastStatus(status);
        setPhase("feedback");
        setTally((current) => ({ ...current, [status]: current[status] + 1 }));
    }

    function handleNext() {
        if (index + 1 >= queue.length) {
            onFinish(tally);
            return;
        }
        setIndex((current) => current + 1);
        setPhase("question");
        setLastStatus(null);
    }

    return (
        <div className={styles.quizCard}>
            <div className={styles.quizHeader}>
                <p className={styles.quizPrompt}>{t("quiz.prompt")}</p>
                <div className={styles.quizProgress}>
                    {t("quiz.progress", { current: index + 1, total: queue.length })}
                </div>
            </div>

            <div className={styles.quizWordRow}>
                <UsuallyKanaWord className={styles.quizWord} word={entry.word} alsoWritten={entry.alsoWritten} />
                {entry.word !== entry.reading && (
                    <span className={styles.quizReading}>
                        {entryRomaji ? `${entry.reading} (${entryRomaji})` : entry.reading}
                    </span>
                )}
                {kanaOnlyRomaji && (
                    <span className={styles.quizRomaji} lang="ja-Latn">
                        {kanaOnlyRomaji}
                    </span>
                )}
            </div>

            {phase === "question" ? (
                <div className={styles.answerRow}>
                    <button type="button" className={styles.knowButton} onClick={() => handleAnswer("memorized")}>
                        <FontAwesomeIcon icon={faCheck} /> {t("quiz.iAlreadyKnow")}
                    </button>
                    <button
                        type="button"
                        className={styles.learningButton}
                        onClick={() => handleAnswer("notMemorized")}
                    >
                        <FontAwesomeIcon icon={faQuestion} /> {t("quiz.iDontKnowYet")}
                    </button>
                </div>
            ) : (
                <div
                    className={
                        lastStatus === "memorized" ? styles.quizFeedbackCorrect : styles.quizFeedbackWrong
                    }
                >
                    <div>{t("quiz.meaning", { meaning: entry.meanings.join(", ") })}</div>
                    {description && <div>{description}</div>}
                    {entry.verbType && (
                        <ConjugationTable
                            word={entry.word}
                            reading={entry.reading}
                            verbType={entry.verbType}
                            showRomaji
                        />
                    )}
                </div>
            )}

            <div className={styles.quizActions}>
                <button type="button" className={styles.quizSecondaryButton} onClick={onExit}>
                    {t("quiz.endRound")}
                </button>
                {phase === "feedback" && (
                    <button type="button" className={styles.quizPrimaryButton} onClick={handleNext}>
                        {index + 1 >= queue.length ? t("quiz.seeResults") : t("quiz.next")}
                    </button>
                )}
            </div>
        </div>
    );
}

function VocabRoundSummary({ tally, onRestart, onExit }) {
    const { t } = useTranslation("vocabularyPage");
    return (
        <div className={styles.quizCard}>
            <h2 className={styles.cardTitle}>{t("summary.title")}</h2>
            <p className={styles.quizPrompt}>
                {t("summary.tally", { memorized: tally.memorized, notMemorized: tally.notMemorized })}
            </p>
            <div className={styles.quizActions}>
                <button type="button" className={styles.quizSecondaryButton} onClick={onExit}>
                    {t("summary.backToList")}
                </button>
                <button type="button" className={styles.quizPrimaryButton} onClick={onRestart}>
                    {t("summary.startAnother")}
                </button>
            </div>
        </div>
    );
}

function VocabularyPage() {
    const { t, i18n } = useTranslation("vocabularyPage");
    const { levels: preferredLevels } = useJlptLevels();
    const [browseLevels, setBrowseLevels] = useState(null);
    const [searchLevels, setSearchLevels] = useState(null);
    const [status, setStatus] = useState("all");
    const [query, setQuery] = useState("");
    const [katakanaOnly, setKatakanaOnly] = useState(false);
    const { progress, setLearned, syncStatus } = useVocabularyProgress();
    const signedOut = syncStatus === "signedOut";
    const isProgressReady = syncStatus !== "loading";
    const {
        counts: listenCounts,
        increment: incrementListenCount,
        syncStatus: listenSyncStatus,
    } = useVocabularyListenCounts();
    const isListenProgressReady = listenSyncStatus !== "loading";

    const [words, setWords] = useState([]);
    const [loadState, setLoadState] = useState("loading"); // loading | ready | error

    const [mode, setMode] = useState("browse"); // browse | quiz | summary
    const [queue, setQueue] = useState([]);
    const [tally, setTally] = useState(null);
    const [viewMode, setViewMode] = useState("flat"); // flat | byKanji
    const [savingWordIds, setSavingWordIds] = useState(() => new Set());
    const savingWordIdsRef = useRef(new Set());

    const isPt = i18n.language === "pt";
    const currentPath = isPt ? "/pt/vocabulary" : "/vocabulary";
    const isSearching = query.trim().length > 0;
    const studyLevels = useMemo(() => browseLevels || preferredLevels, [browseLevels, preferredLevels]);
    const visibleLevels = useMemo(
        () => (isSearching ? searchLevels || levelKeys : studyLevels),
        [isSearching, searchLevels, studyLevels]
    );
    // Keep the learner's intended study levels loaded during a narrowed
    // search, so its progress totals and quiz cannot silently switch scope.
    const levelsToLoad = useMemo(
        () => Array.from(new Set([...visibleLevels, ...studyLevels])),
        [visibleLevels, studyLevels]
    );
    const visibleLevelLabel = visibleLevels.map((level) => `N${level}`).join(" + ");
    const isSearchingAllLevels = isSearching && visibleLevels.length === levelKeys.length;

    useEffect(() => {
        let cancelled = false;
        setLoadState("loading");
        Promise.all(
            levelsToLoad.map((level) =>
                fetchVocabularyLevel(level, i18n.language).then((data) =>
                    data.map((entry) => ({ ...entry, jlptLevel: level }))
                )
            )
        )
            .then((levelWords) => {
                if (cancelled) return;
                setWords(levelWords.flat());
                setLoadState("ready");
            })
            .catch(() => {
                if (!cancelled) setLoadState("error");
            });
        return () => {
            cancelled = true;
        };
    }, [levelsToLoad, i18n.language]);

    function handleLevelChange(key) {
        const setLevels = isSearching ? setSearchLevels : setBrowseLevels;
        if (isSearching && !searchLevels) {
            // A search starts at "all" because that scope is automatic. The
            // first chip pick is an explicit filter, not "all except N…".
            setLevels([key]);
            return;
        }
        const automaticLevels = isSearching ? levelKeys : studyLevels;
        const currentLevels = isSearching ? searchLevels || automaticLevels : browseLevels || automaticLevels;
        const nextLevels = currentLevels.includes(key)
            ? currentLevels.filter((level) => level !== key)
            : [...currentLevels, key];

        // Clearing the last manual choice restores the appropriate automatic
        // scope: study levels while browsing, every JLPT level while searching.
        setLevels(nextLevels.length ? nextLevels : null);
    }

    // Everything the learner can pick on the list: JLPT levels (browsing and
    // searching), the progress filter, the katakana filter and the search text.
    // The list/by-kanji view toggle is a display choice, so it is left alone.
    const hasActiveSelection =
        browseLevels !== null || searchLevels !== null || status !== "all" || katakanaOnly || query !== "";

    function resetSelection() {
        setBrowseLevels(null);
        setSearchLevels(null);
        setStatus("all");
        setKatakanaOnly(false);
        setQuery("");
    }

    function startQuiz() {
        const studyWords = words.filter((word) => studyLevels.includes(word.jlptLevel));
        const nextQueue = buildQueue(status, studyWords, progress);
        if (!nextQueue.length) return;
        setQueue(nextQueue);
        setMode("quiz");
    }

    const countsByStatus = useMemo(() => {
        const studyWords = words.filter((word) => studyLevels.includes(word.jlptLevel));
        const learned = studyWords.filter((w) => progress[w.id] === "learned").length;
        return { all: studyWords.length, learned, unlearned: studyWords.length - learned };
    }, [words, progress, studyLevels]);

    const filteredWords = useMemo(() => {
        let list = words.filter((word) => visibleLevels.includes(word.jlptLevel));
        if (status !== "all") {
            // Progress views describe the learner's intended study set, even
            // while a text search is looking across every JLPT level.
            list = list.filter((word) => studyLevels.includes(word.jlptLevel));
            if (status === "learned") list = list.filter((w) => progress[w.id] === "learned");
            else list = list.filter((w) => progress[w.id] !== "learned");
        }

        const trimmed = query.trim().toLowerCase();
        if (trimmed) {
            list = list.filter(
                (w) =>
                    w.word.includes(trimmed) ||
                    w.alsoWritten?.toLowerCase().includes(trimmed) ||
                    w.reading.includes(trimmed) ||
                    wordMatchesRomaji(w, trimmed) ||
                    wordMatchesMeaning(w, trimmed)
            );
        }
        if (katakanaOnly) list = list.filter(containsKatakana);
        return list;
    }, [words, visibleLevels, studyLevels, status, query, progress, katakanaOnly]);

    const kanjiGroups = useMemo(() => groupWordsByKanji(filteredWords), [filteredWords]);

    async function handleLearnedChange(id, learned) {
        if (savingWordIdsRef.current.has(id)) return false;
        savingWordIdsRef.current.add(id);
        setSavingWordIds(new Set(savingWordIdsRef.current));
        try {
            return await setLearned(id, learned);
        } finally {
            savingWordIdsRef.current.delete(id);
            setSavingWordIds(new Set(savingWordIdsRef.current));
        }
    }

    function renderCard(entry) {
        return (
            <VocabularyCard
                key={`${entry.jlptLevel}:${entry.id}`}
                entry={entry}
                showJlptLevel={isSearching}
                learned={isProgressReady && progress[entry.id] === "learned"}
                isProgressReady={isProgressReady}
                signedOut={signedOut}
                onToggleLearned={handleLearnedChange}
                saving={savingWordIds.has(entry.id)}
                listenCount={listenCounts[entry.id] || 0}
                isListenProgressReady={isListenProgressReady}
                onListen={incrementListenCount}
            />
        );
    }

    return (
        <div className={styles.page}>
            <Seo
                title={t("seoTitle")}
                description={t("seoDescription")}
                path={currentPath}
                lang={isPt ? "pt-PT" : undefined}
            />
            <Header />
            <main className={styles.main}>
                <section className={styles.container}>
                    <h1 className={styles.title}>{t("title")}</h1>
                    <p className={styles.description}>{t("description")}</p>

                    {mode === "browse" && (
                        <>
                            <div className={styles.card}>
                                <div className={styles.cardHeader}>
                                    <h2 className={styles.cardTitle}>{t("filterByLevel")}</h2>
                                    <button
                                        type="button"
                                        className={styles.resetButton}
                                        onClick={resetSelection}
                                        disabled={!hasActiveSelection}
                                    >
                                        <FontAwesomeIcon icon={faRotateLeft} />
                                        {t("resetSelection")}
                                    </button>
                                </div>
                                <div className={styles.filterRow}>
                                    {isSearching && (
                                        <button
                                            type="button"
                                            className={isSearchingAllLevels ? styles.levelChipActive : styles.levelChip}
                                            onClick={() => setSearchLevels(null)}
                                            aria-pressed={isSearchingAllLevels}
                                        >
                                            {t("allLevels")}
                                        </button>
                                    )}
                                    {levelKeys.map((key) => (
                                        <button
                                            key={key}
                                            type="button"
                                            className={visibleLevels.includes(key) ? styles.levelChipActive : styles.levelChip}
                                            onClick={() => handleLevelChange(key)}
                                            aria-pressed={visibleLevels.includes(key)}
                                        >
                                            N{key}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        className={katakanaOnly ? styles.levelChipActive : styles.levelChip}
                                        onClick={() => setKatakanaOnly((current) => !current)}
                                        aria-pressed={katakanaOnly}
                                    >
                                        {t("katakanaFilter")}
                                    </button>
                                </div>
                            </div>

                            <div className={styles.card}>
                                <h2 className={styles.cardTitle}>{t("filterByProgress")}</h2>
                                {isProgressReady ? (
                                    <>
                                        <p className={styles.cardText}>{t("filterByProgressHelp")}</p>
                                        <div className={styles.filterRow}>
                                            {statusKeys.map((key) => (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    className={
                                                        key === status ? styles.levelChipActive : styles.levelChip
                                                    }
                                                    onClick={() => setStatus(key)}
                                                    disabled={countsByStatus[key] === 0}
                                                >
                                                    {t(`statuses.${key}`)} ({countsByStatus[key]})
                                                </button>
                                            ))}
                                        </div>
                                        <div className={styles.actionsRow}>
                                            <button
                                                type="button"
                                                className={`${styles.quizPrimaryButton} ${styles.startButton}`}
                                                onClick={startQuiz}
                                                disabled={countsByStatus[status] === 0}
                                            >
                                                {t("startStudying", { levels: studyLevels.map((level) => `N${level}`).join(" + ") })}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <p className={styles.emptyText}>{t("syncingProgress")}</p>
                                )}
                            </div>

                            <input
                                type="search"
                                className={styles.searchInput}
                                placeholder={t("searchPlaceholder")}
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                            />

                            {isSearchingAllLevels && (
                                <p className={styles.searchScope} role="status">
                                    {t("searchingAllLevels")}
                                </p>
                            )}

                            {loadState === "loading" && (
                                <p className={styles.emptyText}>{t("loadingVocabulary", { levels: visibleLevelLabel })}</p>
                            )}
                            {loadState === "error" && (
                                <p className={styles.emptyText}>{t("loadError")}</p>
                            )}
                            {loadState === "ready" && filteredWords.length === 0 && (
                                <p className={styles.emptyText}>{t("noMatches")}</p>
                            )}

                            {loadState === "ready" && filteredWords.length > 0 && (
                                <div className={styles.filterRow} role="group" aria-label={t("viewMode.label")}>
                                    {viewModeKeys.map((key) => (
                                        <button
                                            key={key}
                                            type="button"
                                            className={key === viewMode ? styles.levelChipActive : styles.levelChip}
                                            onClick={() => setViewMode(key)}
                                            aria-pressed={key === viewMode}
                                        >
                                            {t(`viewMode.${key}`)}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {loadState === "ready" && filteredWords.length > 0 && viewMode === "flat" && (
                                <ul className={styles.vocabList}>{filteredWords.map((entry) => renderCard(entry))}</ul>
                            )}

                            {loadState === "ready" && filteredWords.length > 0 && viewMode === "byKanji" && (
                                <div className={styles.kanjiGroups}>
                                    {kanjiGroups.groups.map((group) => (
                                        <section key={group.kanji} className={styles.kanjiGroup}>
                                            <h3 className={styles.kanjiGroupHeading}>
                                                <span className={styles.kanjiGroupKanji}>{group.kanji}</span>
                                                <span className={styles.kanjiGroupCount}>
                                                    {t("kanjiGroups.wordCount", { count: group.words.length })}
                                                </span>
                                            </h3>
                                            <ul className={styles.vocabList}>
                                                {group.words.map((entry) => renderCard(entry))}
                                            </ul>
                                        </section>
                                    ))}
                                    {kanjiGroups.noKanji.length > 0 && (
                                        <section className={styles.kanjiGroup}>
                                            <h3 className={styles.kanjiGroupHeading}>
                                                <span className={styles.kanjiGroupLabel}>
                                                    {t("kanjiGroups.noKanji")}
                                                </span>
                                                <span className={styles.kanjiGroupCount}>
                                                    {t("kanjiGroups.wordCount", { count: kanjiGroups.noKanji.length })}
                                                </span>
                                            </h3>
                                            <ul className={styles.vocabList}>
                                                {kanjiGroups.noKanji.map((entry) => renderCard(entry))}
                                            </ul>
                                        </section>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {mode === "quiz" && (
                        <VocabQuizRound
                            queue={queue}
                            onMark={handleLearnedChange}
                            onExit={() => setMode("browse")}
                            onFinish={(finalTally) => {
                                setTally(finalTally);
                                setMode("summary");
                            }}
                        />
                    )}

                    {mode === "summary" && (
                        <VocabRoundSummary tally={tally} onExit={() => setMode("browse")} onRestart={startQuiz} />
                    )}

                    <p className={styles.attribution}>
                        {t("attribution.wordsFrom")}{" "}
                        <a href="https://www.edrdg.org/wiki/index.php/JMdict-EDICT_Dictionary_Project" target="_blank" rel="noreferrer">
                            JMdict
                        </a>
                        {t("attribution.copyright")}{" "}
                        <a href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noreferrer">
                            CC BY-SA 4.0
                        </a>{" "}
                        {t("attribution.licence")}{" "}
                        <a href="https://www.tanos.co.uk/jlpt/" target="_blank" rel="noreferrer">
                            Jonathan Waller's JLPT resources
                        </a>
                        {t("attribution.usedUnder")}{" "}
                        <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noreferrer">
                            CC BY 4.0
                        </a>{" "}
                        {t("attribution.licenceEnd")}
                    </p>
                </section>
            </main>
            <Footer />
        </div>
    );
}

export default VocabularyPage;
